import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import { Loader2, Lock, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Page reached from the "Set my password" link in the guest receipt email.
 *
 * Flow:
 *  1. On load, POST `/auth/users/claim-account/verify` to validate the token
 *     without consuming it; surface email + first name to greet the parent.
 *  2. Parent enters a password, hits Submit -> POST `/auth/users/claim-account/set-password`.
 *  3. Backend returns JWT pair, we hand them to AuthContext, then redirect
 *     to /parent/enrollments where their bookings already live (because the
 *     User row was provisioned during the original Stripe checkout).
 */
export default function ClaimAccountPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthFromTokens } = useAuth();

  const token = params.get('token');
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [accountInfo, setAccountInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) {
        setError('Missing claim token. Please use the link in your confirmation email.');
        setVerifying(false);
        return;
      }
      try {
        const { data } = await axios.post(
          `${API}/auth/claim-account/verify`,
          { token },
          { withCredentials: true },
        );
        if (!active) return;
        setAccountInfo(data);
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.detail || 'This claim link has expired or already been used.');
      } finally {
        if (active) setVerifying(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/claim-account/set-password`,
        { token, new_password: password },
        { withCredentials: true },
      );
      // Drop the parent into their dashboard with their JWT.
      if (setAuthFromTokens) {
        setAuthFromTokens({
          access: data.access_token,
          refresh: data.refresh_token,
          user: data.user,
        });
      }
      setSuccess(true);
      toast.success('Account claimed — welcome back!');
      setTimeout(() => navigate('/parent/enrollments'), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not claim this account.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="section-padding">
      <div className="container-app max-w-md">
        <Card className="card-marketing">
          <CardContent className="py-10">
            {verifying && (
              <div className="text-center" data-testid="claim-loading">
                <Loader2 className="h-10 w-10 text-primary mx-auto mb-4 animate-spin" />
                <p className="text-muted-foreground">Checking your link…</p>
              </div>
            )}

            {!verifying && error && (
              <div className="text-center" data-testid="claim-error">
                <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                </div>
                <h1 className="font-heading text-2xl font-bold mb-2">Link unavailable</h1>
                <p className="text-muted-foreground mb-6">{error}</p>
                <Button onClick={() => navigate('/login')} data-testid="claim-error-login-btn">
                  Sign in instead
                </Button>
              </div>
            )}

            {!verifying && !error && success && (
              <div className="text-center" data-testid="claim-success">
                <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8 text-accent" />
                </div>
                <h1 className="font-heading text-2xl font-bold mb-2">You're in!</h1>
                <p className="text-muted-foreground">Loading your bookings…</p>
              </div>
            )}

            {!verifying && !error && !success && accountInfo && (
              <div data-testid="claim-form">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <Lock className="h-7 w-7 text-primary" />
                  </div>
                  <h1 className="font-heading text-2xl font-bold mb-1">
                    Welcome, {accountInfo.first_name || 'there'}!
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Set a password for <strong>{accountInfo.email}</strong> to manage your bookings.
                  </p>
                </div>

                <form onSubmit={onSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="claim-password">New password</Label>
                    <Input
                      id="claim-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      minLength={8}
                      required
                      data-testid="claim-password-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="claim-confirm">Confirm password</Label>
                    <Input
                      id="claim-confirm"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      autoComplete="new-password"
                      minLength={8}
                      required
                      data-testid="claim-confirm-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="btn-primary w-full"
                    disabled={submitting}
                    data-testid="claim-submit-btn"
                  >
                    {submitting ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Setting password…</>
                    ) : (
                      'Set password & view bookings'
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    Already have an account?{' '}
                    <button
                      type="button"
                      className="underline"
                      onClick={() => navigate('/login')}
                      data-testid="claim-existing-login-link"
                    >
                      Sign in
                    </button>
                  </p>
                </form>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
